import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import {
  PageHeader, Spinner, EmptyState, inputCls, ScoreBadge, StageBadge, PlatformTag,
  STAGE_LABELS, PIPELINE_STAGES, PLATFORM_META,
} from "@/components/app/widgets";

export default function Prospects() {
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ platform: "", status: "", min_score: "", q: "", analysis_status: "", sort: "" });
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PER_PAGE = 100;

  const load = (pg = page) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    params.set("limit", String(PER_PAGE));
    params.set("skip", String(pg * PER_PAGE));
    api.get(`/prospects?${params.toString()}`)
      .then((r) => { setItems(r.data.items); setTotal(r.data.total ?? r.data.items.length); })
      .catch(() => setItems([]));
  };
  const goTo = (pg) => { setPage(pg); load(pg); };
  const submitSearch = () => { setPage(0); load(0); };
  useEffect(() => { setPage(0); load(0); }, [filters.platform, filters.status, filters.min_score, filters.analysis_status, filters.sort]);

  if (!items) return <Spinner label="Loading prospects" />;

  return (
    <div data-testid="prospects-page">
      <PageHeader title="Prospect Database" sub="Every discovered audience owner across all campaigns — with provenance." />

      <div className="mb-6 flex flex-wrap gap-3">
        <input
          type="text" placeholder="Search name, description…" value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && submitSearch()}
          onBlur={submitSearch}
          data-testid="prospect-search-input"
          className={`${inputCls} w-64`}
        />
        <select value={filters.platform} onChange={(e) => setFilters({ ...filters, platform: e.target.value })} data-testid="filter-platform" className={`${inputCls} w-40`}>
          <option value="">All platforms</option>
          {Object.entries(PLATFORM_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status" className={`${inputCls} w-44`}>
          <option value="">All stages</option>
          {PIPELINE_STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
        </select>
        <select value={filters.min_score} onChange={(e) => setFilters({ ...filters, min_score: e.target.value })} data-testid="filter-min-score" className={`${inputCls} w-40`}>
          <option value="">Any score</option>
          <option value="75">75+ (high)</option>
          <option value="50">50+ (medium)</option>
        </select>
        <select value={filters.analysis_status} onChange={(e) => setFilters({ ...filters, analysis_status: e.target.value })} data-testid="filter-analysis-status" className={`${inputCls} w-44`}>
          <option value="">Any research status</option>
          <option value="ANALYZED">Analyzed</option>
          <option value="NOT_ANALYZED">Not analyzed</option>
          <option value="PROCESSING">Processing</option>
          <option value="FAILED">Failed</option>
          <option value="NEEDS_REVIEW">Needs review</option>
        </select>
        <select value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })} data-testid="filter-sort" className={`${inputCls} w-52`}>
          <option value="">Sort: newest</option>
          <option value="relevance">Sort: discovery relevance</option>
          <option value="score">Sort: opportunity score</option>
          <option value="audience_fit">Sort: audience fit</option>
          <option value="commercial_potential">Sort: commercial potential</option>
        </select>
        <button
          onClick={() => setFilters({ ...filters, min_score: "75", sort: "score" })}
          data-testid="top-opportunities-filter"
          className="rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/20"
        >
          Top Opportunities
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Users} title="No prospects match" desc="Run discovery from a campaign or adjust your filters." />
      ) : (
        <div className="overflow-x-auto border border-[#27272A]">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#0A0A0A] text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-5 py-3">Prospect</th>
                <th className="px-5 py-3">Platform</th>
                <th className="px-5 py-3">Score</th>
                <th className="px-5 py-3">Stage</th>
                <th className="px-5 py-3">Source</th>
                <th className="px-5 py-3">Research</th>
                <th className="px-5 py-3">Next Action</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((p) => (
                <tr key={p.prospect_id} data-testid={`prospect-row-${p.prospect_id}`} className="transition-colors hover:bg-white/[0.03]">
                  <td className="max-w-[260px] px-5 py-3.5">
                    <p className="truncate font-medium text-white">{p.name}</p>
                    {p.creator && <p className="truncate text-xs text-zinc-500">{p.creator}</p>}
                  </td>
                  <td className="px-5 py-3.5"><PlatformTag platform={p.platform} /></td>
                  <td className="px-5 py-3.5"><ScoreBadge score={p.score?.total} /></td>
                  <td className="px-5 py-3.5"><StageBadge stage={p.status} /></td>
                  <td className="px-5 py-3.5 text-xs text-zinc-500">{p.source?.provider}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${
                      p.analysis?.status === "ANALYZED" ? "text-emerald-400"
                      : p.analysis?.status === "PROCESSING" ? "text-[#D4AF37]"
                      : p.analysis?.status === "FAILED" ? "text-red-400"
                      : "text-zinc-600"
                    }`}>
                      {(p.analysis?.status || "NOT_ANALYZED").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="max-w-[220px] px-5 py-3.5">
                    <p className="truncate text-xs text-zinc-400">{p.next_action?.action || "—"}</p>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <Link to={`/app/prospects/${p.prospect_id}`} data-testid={`open-prospect-${p.prospect_id}`} className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:brightness-110">
                      Open <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > 0 && (
        <div className="mt-4 flex items-center justify-between text-xs text-zinc-500">
          <span data-testid="prospects-page-info">
            Showing {Math.min(page * PER_PAGE + 1, total)}–{Math.min((page + 1) * PER_PAGE, total)} of {total} prospects
          </span>
          {total > PER_PAGE && (
            <div className="flex gap-2">
              <button
                onClick={() => goTo(page - 1)} disabled={page === 0}
                data-testid="prospects-prev-page"
                className="border border-white/10 px-3 py-1.5 font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:border-[#D4AF37]/50 disabled:opacity-30"
              >Previous</button>
              <button
                onClick={() => goTo(page + 1)} disabled={(page + 1) * PER_PAGE >= total}
                data-testid="prospects-next-page"
                className="border border-white/10 px-3 py-1.5 font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:border-[#D4AF37]/50 disabled:opacity-30"
              >Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
